import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companiesApi } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import ExternalCredentials from '../components/ExternalCredentials';
import PersonMasterSearchModal from '../components/PersonMasterSearchModal';
import {
  Building2, ArrowLeft, Loader2, Save, X, Pencil, MapPin, Shield,
  CreditCard, Key, Users, UserPlus, Trash2, ChevronDown, ChevronUp,
  Globe, Phone, Mail, FileText, Hash, Calendar, User, Plus
} from 'lucide-react';

const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-700/30 transition-colors"
        data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <div className="flex items-center gap-2.5">
          <Icon className="w-5 h-5 text-emerald-400" />
          <h3 className="text-base font-semibold text-white">{title}</h3>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4 border-t border-slate-700/50 pt-4">{children}</div>}
    </div>
  );
};

const Field = ({ label, value, editing, editValue, onChange, type = 'text', placeholder = '' }) => (
  <div>
    <label className="text-xs text-slate-400 block mb-1">{label}</label>
    {editing ? (
      <Input type={type} value={editValue ?? ''} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} className="bg-slate-900 border-slate-700 text-white text-sm" />
    ) : (
      <p className="text-sm text-white bg-slate-900/50 rounded-lg px-3 py-2 border border-slate-700/30 min-h-[36px]">
        {value || <span className="text-slate-500 italic">Not set</span>}
      </p>
    )}
  </div>
);

const TextAreaField = ({ label, value, editing, editValue, onChange, placeholder = '' }) => (
  <div>
    <label className="text-xs text-slate-400 block mb-1">{label}</label>
    {editing ? (
      <textarea value={editValue ?? ''} onChange={(e) => onChange(e.target.value)} rows={3}
        placeholder={placeholder} className="w-full rounded-md bg-slate-900 border border-slate-700 text-white text-sm p-2 resize-none" />
    ) : (
      <p className="text-sm text-white bg-slate-900/50 rounded-lg px-3 py-2 border border-slate-700/30 min-h-[60px] whitespace-pre-wrap">
        {value || <span className="text-slate-500 italic">Not set</span>}
      </p>
    )}
  </div>
);

// Director Card
const DirectorCard = ({ director, onEdit, onRemove }) => {
  return (
    <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-4" data-testid={`director-card-${director.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4 text-indigo-400" />
            <h4 className="text-white font-medium">{director.full_name}</h4>
            {director.din_number && <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">DIN: {director.din_number}</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
            {director.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-500" /><span className="text-slate-300">{director.email}</span></div>}
            {director.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-500" /><span className="text-slate-300">{director.phone}</span></div>}
            {director.pan_number && <div className="flex items-center gap-2"><Hash className="w-3.5 h-3.5 text-slate-500" /><span className="text-slate-300">PAN: {director.pan_number}</span></div>}
            {director.date_of_birth && <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-slate-500" /><span className="text-slate-300">DOB: {director.date_of_birth}</span></div>}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => onEdit(director)} className="p-2 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors" data-testid={`edit-director-${director.id}`}>
            <Pencil className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Shareholder Card
const ShareholderCard = ({ sh, onEdit }) => (
  <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-4" data-testid={`shareholder-card-${sh.id}`}>
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <User className="w-4 h-4 text-amber-400" />
          <h4 className="text-white font-medium">{sh.full_name}</h4>
          {sh.percent_holding != null && <span className="text-xs bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">{sh.percent_holding}%</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
          {sh.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-500" /><span className="text-slate-300">{sh.email}</span></div>}
          {sh.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-500" /><span className="text-slate-300">{sh.phone}</span></div>}
          {sh.number_of_shares && <div className="flex items-center gap-2"><Hash className="w-3.5 h-3.5 text-slate-500" /><span className="text-slate-300">{sh.number_of_shares} shares</span></div>}
        </div>
      </div>
      <button onClick={() => onEdit(sh)} className="p-2 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors" data-testid={`edit-shareholder-${sh.id}`}>
        <Pencil className="w-4 h-4" />
      </button>
    </div>
  </div>
);

// Director Add/Edit Modal
const DirectorModal = ({ director, onSave, onClose, saving }) => {
  const [form, setForm] = useState({
    full_name: director?.full_name || '',
    email: director?.email || '',
    phone: director?.phone || '',
    pan_number: director?.pan_number || '',
    aadhaar_number: director?.aadhaar_number || '',
    din_number: director?.din_number || '',
    date_of_birth: director?.date_of_birth || '',
    address: director?.address || '',
    father_name: director?.father_name || '',
    nationality: director?.nationality || 'Indian',
    occupation: director?.occupation || '',
  });
  const isEdit = !!director?.id;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" data-testid="director-modal">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{isEdit ? 'Edit Director' : 'Add Director'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {[
            { key: 'full_name', label: 'Full Name *', placeholder: 'John Doe' },
            { key: 'email', label: 'Email *', placeholder: 'john@example.com', type: 'email' },
            { key: 'phone', label: 'Phone *', placeholder: '+91 9876543210' },
            { key: 'pan_number', label: 'PAN Number', placeholder: 'ABCDE1234F' },
            { key: 'aadhaar_number', label: 'Aadhaar Number', placeholder: '1234 5678 9012' },
            { key: 'din_number', label: 'DIN Number', placeholder: '12345678' },
            { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
            { key: 'father_name', label: "Father's Name", placeholder: '' },
            { key: 'address', label: 'Address', placeholder: '' },
            { key: 'occupation', label: 'Occupation', placeholder: '' },
          ].map(f => (
            <div key={f.key}>
              <label className="text-xs text-slate-400 block mb-1">{f.label}</label>
              <Input type={f.type || 'text'} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                placeholder={f.placeholder || ''} className="bg-slate-900 border-slate-700 text-white text-sm" />
            </div>
          ))}
        </div>
        <div className="p-5 border-t border-slate-700 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">Cancel</Button>
          <Button onClick={() => onSave(form, isEdit ? director.id : null)} disabled={saving || !form.full_name || !form.email || !form.phone}
            className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-director-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {isEdit ? 'Update' : 'Add Director'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Shareholder Add/Edit Modal
const ShareholderModal = ({ sh, onSave, onClose, saving }) => {
  const [form, setForm] = useState({
    full_name: sh?.full_name || '',
    email: sh?.email || '',
    phone: sh?.phone || '',
    pan_number: sh?.pan_number || '',
    aadhaar_number: sh?.aadhaar_number || '',
    number_of_shares: sh?.number_of_shares || 0,
    date_of_birth: sh?.date_of_birth || '',
    address: sh?.address || '',
    father_name: sh?.father_name || '',
    nationality: sh?.nationality || 'Indian',
    category: sh?.category || 'individual',
  });
  const isEdit = !!sh?.id;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" data-testid="shareholder-modal">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{isEdit ? 'Edit Shareholder' : 'Add Shareholder'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {[
            { key: 'full_name', label: 'Full Name *', placeholder: 'Jane Doe' },
            { key: 'email', label: 'Email *', placeholder: 'jane@example.com', type: 'email' },
            { key: 'phone', label: 'Phone *', placeholder: '+91 9876543210' },
            { key: 'number_of_shares', label: 'Number of Shares *', type: 'number' },
            { key: 'pan_number', label: 'PAN Number', placeholder: 'ABCDE1234F' },
            { key: 'aadhaar_number', label: 'Aadhaar Number', placeholder: '1234 5678 9012' },
            { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
            { key: 'father_name', label: "Father's Name" },
            { key: 'address', label: 'Address' },
          ].map(f => (
            <div key={f.key}>
              <label className="text-xs text-slate-400 block mb-1">{f.label}</label>
              <Input type={f.type || 'text'} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: f.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value })}
                placeholder={f.placeholder || ''} className="bg-slate-900 border-slate-700 text-white text-sm" />
            </div>
          ))}
        </div>
        <div className="p-5 border-t border-slate-700 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">Cancel</Button>
          <Button onClick={() => onSave(form, isEdit ? sh.id : null)} disabled={saving || !form.full_name || !form.email || !form.phone}
            className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-shareholder-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {isEdit ? 'Update' : 'Add Shareholder'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function CompanyProfilePage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [directors, setDirectors] = useState([]);
  const [shareholders, setShareholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Director/Shareholder modal state
  const [directorModal, setDirectorModal] = useState(null); // null | {} (new) | director obj (edit)
  const [showMasterSearch, setShowMasterSearch] = useState(false); // Stage 1: "Search master first" step
  const [shareholderModal, setShareholderModal] = useState(null);
  const [modalSaving, setModalSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [comp, dirs, shs] = await Promise.all([
        companiesApi.getById(companyId),
        companiesApi.getDirectors(companyId),
        companiesApi.getShareholders(companyId),
      ]);
      setCompany(comp);
      setDirectors(dirs);
      setShareholders(shs);
    } catch (err) {
      toast.error('Failed to load company data');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [companyId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEditing = () => {
    setEditForm({
      description: company?.description || '',
      registered_address: company?.registered_address || '',
      business_activity: company?.business_activity || '',
      state: company?.state || '',
      police_station_jurisdiction: company?.police_station_jurisdiction || '',
      google_location_lat: company?.google_location_lat || '',
      google_location_lng: company?.google_location_lng || '',
      gstin: company?.gstin || '',
      gst_state_code: company?.gst_state_code || '',
      authorized_capital: company?.capital_structure?.authorized_capital || 0,
      paid_up_capital: company?.capital_structure?.paid_up_capital || 0,
      share_face_value: company?.capital_structure?.share_face_value || 10,
      v3_username: company?.v3_username || '',
      v3_password: company?.v3_password || '',
      v3_hint_question: company?.v3_hint_question || '',
      company_phone: company?.company_phone || '',
      company_email: company?.company_email || '',
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companiesApi.update(companyId, {
        description: editForm.description || null,
        registered_address: editForm.registered_address || null,
        business_activity: editForm.business_activity || null,
        state: editForm.state || null,
        police_station_jurisdiction: editForm.police_station_jurisdiction || null,
        google_location_lat: editForm.google_location_lat || null,
        google_location_lng: editForm.google_location_lng || null,
        gstin: editForm.gstin || null,
        gst_state_code: editForm.gst_state_code || null,
        capital_structure: {
          authorized_capital: parseInt(editForm.authorized_capital) || 0,
          paid_up_capital: parseInt(editForm.paid_up_capital) || 0,
          share_face_value: parseInt(editForm.share_face_value) || 10,
        },
        v3_username: editForm.v3_username || null,
        v3_password: editForm.v3_password || null,
        v3_hint_question: editForm.v3_hint_question || null,
        company_phone: editForm.company_phone || null,
        company_email: editForm.company_email || null,
      });
      toast.success('Company profile updated');
      setEditing(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  // Director CRUD
  const handleSaveDirector = async (form, directorId) => {
    setModalSaving(true);
    try {
      if (directorId) {
        await api.put(`/directors/${directorId}`, form);
        toast.success('Director updated');
      } else {
        // 1) Create the classic director + link to company (unchanged legacy flow)
        const res = await api.post('/directors', form);
        await api.post(`/companies/${companyId}/directors/${res.data.id}`);

        // 2) Stage 1: also create a Person Master and link the director to it,
        //    so future reappointments of this person are searchable and blocked
        //    from duplicating. Best-effort — legacy add still succeeds if this fails.
        try {
          const master = await api.post('/admin/person-masters', {
            full_name: form.full_name,
            din: form.din_number || null,
            pan: form.pan_number || null,
            email: form.email || null,
            phone: form.phone || null,
            date_of_birth: form.date_of_birth || null,
            father_name: form.father_name || null,
            occupation: form.occupation || null,
          });
          await api.post(`/admin/directors/${res.data.id}/link-person`, {
            person_master_id: master.data.id,
          });
        } catch (masterErr) {
          // If the master already exists (409 on DIN/PAN), find it and link.
          try {
            const q = form.din_number || form.pan_number || form.email || form.full_name;
            if (q) {
              const search = await api.get('/admin/person-masters/search', { params: { q } });
              const found = (search.data.items || [])[0];
              if (found) {
                await api.post(`/admin/directors/${res.data.id}/link-person`, {
                  person_master_id: found.id,
                });
              }
            }
          } catch (_) {
            // silent — legacy flow already succeeded
          }
        }

        toast.success('Director added');
      }
      setDirectorModal(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save director');
    } finally {
      setModalSaving(false);
    }
  };

  // Shareholder CRUD
  const handleSaveShareholder = async (form, shId) => {
    setModalSaving(true);
    try {
      if (shId) {
        await companiesApi.updateShareholder(shId, form);
        toast.success('Shareholder updated');
      } else {
        await companiesApi.addShareholder(companyId, form);
        toast.success('Shareholder added');
      }
      setShareholderModal(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save shareholder');
    } finally {
      setModalSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!company) return null;

  const cap = company.capital_structure || {};

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-slate-400 hover:text-white -ml-2">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <Button variant="outline" size="sm" onClick={() => setEditing(false)} className="border-slate-600 text-slate-300">
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-profile-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Changes
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={startEditing} className="bg-indigo-600 hover:bg-indigo-700" data-testid="edit-profile-btn">
                <Pencil className="w-4 h-4 mr-1" /> Edit Profile
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Company name header */}
        <div className="flex items-center gap-4 mb-2">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{company.company_name}</h1>
            <p className="text-sm text-slate-400">
              {company.entity_type?.replace(/_/g, ' ')} &middot; {company.state} &middot; {company.cin_llpin || 'No CIN/LLPIN'}
            </p>
          </div>
        </div>

        {/* Section 1: Description & Registered Address */}
        <Section title="Description & Registered Address" icon={FileText}>
          <div className="space-y-3">
            <TextAreaField label="Description" value={company.description} editing={editing}
              editValue={editForm.description} onChange={(v) => setEditForm({ ...editForm, description: v })} placeholder="Company description..." />
            <TextAreaField label="Registered Address" value={company.registered_address} editing={editing}
              editValue={editForm.registered_address} onChange={(v) => setEditForm({ ...editForm, registered_address: v })} placeholder="Registered office address..." />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Business Activity" value={company.business_activity} editing={editing}
                editValue={editForm.business_activity} onChange={(v) => setEditForm({ ...editForm, business_activity: v })} />
              <Field label="State" value={company.state} editing={editing}
                editValue={editForm.state} onChange={(v) => setEditForm({ ...editForm, state: v })} />
              <Field label="Phone" value={company.company_phone} editing={editing}
                editValue={editForm.company_phone} onChange={(v) => setEditForm({ ...editForm, company_phone: v })} />
              <Field label="Email" value={company.company_email} editing={editing}
                editValue={editForm.company_email} onChange={(v) => setEditForm({ ...editForm, company_email: v })} type="email" />
            </div>
          </div>
        </Section>

        {/* Section 2: Location & Jurisdiction */}
        <Section title="Location & Jurisdiction" icon={MapPin}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Police Station Jurisdiction" value={company.police_station_jurisdiction} editing={editing}
              editValue={editForm.police_station_jurisdiction} onChange={(v) => setEditForm({ ...editForm, police_station_jurisdiction: v })} />
            <Field label="Google Location (Lat)" value={company.google_location_lat} editing={editing}
              editValue={editForm.google_location_lat} onChange={(v) => setEditForm({ ...editForm, google_location_lat: v })} />
            <Field label="Google Location (Lng)" value={company.google_location_lng} editing={editing}
              editValue={editForm.google_location_lng} onChange={(v) => setEditForm({ ...editForm, google_location_lng: v })} />
          </div>
        </Section>

        {/* Section 3: GST Number */}
        <Section title="GST Number" icon={CreditCard}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="GSTIN" value={company.gstin} editing={editing}
              editValue={editForm.gstin} onChange={(v) => setEditForm({ ...editForm, gstin: v })} placeholder="22AAAAA0000A1Z5" />
            <Field label="GST State Code" value={company.gst_state_code} editing={editing}
              editValue={editForm.gst_state_code} onChange={(v) => setEditForm({ ...editForm, gst_state_code: v })} />
          </div>
        </Section>

        {/* Section 4: Capital Structure */}
        <Section title="Capital Structure" icon={CreditCard}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Authorized Capital (INR)" value={cap.authorized_capital?.toLocaleString()} editing={editing}
              editValue={editForm.authorized_capital} onChange={(v) => setEditForm({ ...editForm, authorized_capital: v })} type="number" />
            <Field label="Paid-up Capital (INR)" value={cap.paid_up_capital?.toLocaleString()} editing={editing}
              editValue={editForm.paid_up_capital} onChange={(v) => setEditForm({ ...editForm, paid_up_capital: v })} type="number" />
            <Field label="Share Face Value (INR)" value={cap.share_face_value?.toLocaleString()} editing={editing}
              editValue={editForm.share_face_value} onChange={(v) => setEditForm({ ...editForm, share_face_value: v })} type="number" />
          </div>
        </Section>

        {/* Section 5: V3 Credentials */}
        <Section title="V3 Credentials" icon={Key}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="V3 Username" value={company.v3_username} editing={editing}
              editValue={editForm.v3_username} onChange={(v) => setEditForm({ ...editForm, v3_username: v })} />
            <Field label="V3 Password" value={company.v3_password ? '••••••••' : ''} editing={editing}
              editValue={editForm.v3_password} onChange={(v) => setEditForm({ ...editForm, v3_password: v })} />
            <Field label="V3 Hint Question" value={company.v3_hint_question} editing={editing}
              editValue={editForm.v3_hint_question} onChange={(v) => setEditForm({ ...editForm, v3_hint_question: v })} />
          </div>
        </Section>

        {/* Section 6: External Credentials */}
        <ExternalCredentials entityType="company" entityId={companyId} entityName={company.company_name} />

        {/* Section 7: Directors */}
        <Section title={`Directors (${directors.length})`} icon={Users}>
          <div className="space-y-3">
            {directors.length > 0 ? (
              directors.map(d => (
                <DirectorCard key={d.id} director={d} onEdit={(dir) => setDirectorModal(dir)} onRemove={() => {}} />
              ))
            ) : (
              <div className="text-center py-6 border-2 border-dashed border-slate-700/50 rounded-lg">
                <Users className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-500">No directors linked</p>
              </div>
            )}
            <Button size="sm" onClick={() => setShowMasterSearch(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-director-btn">
              <UserPlus className="w-4 h-4 mr-1" /> Add Director
            </Button>
          </div>
        </Section>

        {/* Section 8: Shareholders */}
        <Section title={`Shareholders (${shareholders.length})`} icon={Users}>
          <div className="space-y-3">
            {shareholders.length > 0 ? (
              shareholders.map(s => (
                <ShareholderCard key={s.id} sh={s} onEdit={(sh) => setShareholderModal(sh)} />
              ))
            ) : (
              <div className="text-center py-6 border-2 border-dashed border-slate-700/50 rounded-lg">
                <Users className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-500">No shareholders added</p>
              </div>
            )}
            <Button size="sm" onClick={() => setShareholderModal({})} className="bg-amber-600 hover:bg-amber-700" data-testid="add-shareholder-btn">
              <UserPlus className="w-4 h-4 mr-1" /> Add Shareholder
            </Button>
          </div>
        </Section>
      </main>

      {/* Director Modal */}
      {directorModal !== null && (
        <DirectorModal director={directorModal} onSave={handleSaveDirector} onClose={() => setDirectorModal(null)} saving={modalSaving} />
      )}

      {/* Stage 1: Search Master First — precedes the classic DirectorModal */}
      {showMasterSearch && (
        <PersonMasterSearchModal
          companyId={companyId}
          onClose={() => setShowMasterSearch(false)}
          onDone={() => fetchData()}
          onCreateNew={() => setDirectorModal({})}
        />
      )}

      {/* Shareholder Modal */}
      {shareholderModal !== null && (
        <ShareholderModal sh={shareholderModal} onSave={handleSaveShareholder} onClose={() => setShareholderModal(null)} saving={modalSaving} />
      )}
    </div>
  );
}
